import { Component, inject, Input, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { faAngleDown, faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { NgbActiveModal, NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { delay, finalize } from 'rxjs';
import { AuthService } from 'src/app/auth/auth.service';
import { ParametroTarea, TareaReporte } from 'src/app/features/tasks/tarea-reporte';
import { ModalConfig } from 'src/app/shared/components/modal/modal-config';
import { CustomInput } from 'src/app/shared/components/type-input/custom-input';
import { GlobalService } from 'src/app/shared/services/global.service';
import { REPORT_MESSAGES } from 'src/app/util/constantes-mensajes';
import { HelperFunctions } from 'src/app/util/util-functions';
import { v4 as uuidv4 } from 'uuid';
import { ParametroReporte, TipoParametro } from '../models/parametro';
import { Reporte } from '../models/reporte';
import { ReportsService } from '../reports.service';

@Component({
  selector: 'app-report-parameters',
  templateUrl: './report-parameters.component.html',
  styleUrls: ['./report-parameters.component.scss'],
  standalone: false
})
export class ReportParametersComponent implements OnInit {
  //Report data that user selected
  @Input() report: Reporte;
  //Config to set modal properties
  public modalConfig: ModalConfig;
  //Form group of report Data to save parameters data
  public reportForm: FormGroup;
  //Parameter types to set control inputs
  parameterType: typeof TipoParametro = TipoParametro;
  //Variable to show loader
  isLoading = false;
  //Font awesome icon faCircleXmark
  faCircleXmark = faCircleXmark;
  //Font awesome icon faAngleDown
  faAngleDown = faAngleDown;

  globalService = inject(GlobalService);

  constructor(private reportService: ReportsService, private ngbActiveModal: NgbActiveModal, private authService: AuthService, private ngxUiLoaderService: NgxUiLoaderService) { }


  ngOnInit() {
    this.modalConfig = {
      modalTitle: this.report.nombre,
      ...this.modalConfig,
    };
    this.reportForm = new FormGroup({
      id: new FormControl(this.report.uuid),
      parameters: new FormArray([]),
    });
    this.sortReports();
    this.initParametersForm();
  }
  /**
   * A function that sorts reports by order property
   * @returns None
   */
  sortReports(): void {
    this.report.parametros?.sort((a, b) => {
      return a.orden - b.orden;
    });
  }

  /**
   * A function that sets the initial data of the parameters form array
   * @returns {void}
   */
  initParametersForm(): void {
    this.report.parametros?.forEach((param) => {
      if (param?.parametro) {
        const paramList = param.parametroLista;
        const isMultiple = param.multipleValor;
        let defaultValue;
        if (paramList?.valores?.find(v => v === param.valorXDefecto) || !paramList) {
          defaultValue = param.valorXDefecto;
        }
        this.parameters.push(
          new FormGroup({
            id: new FormControl(param.parametro.uuid),
            type: new FormControl(param.parametro.tipoParametro),
            name: new FormControl(param.parametro.nombre),
            isMultiple: new FormControl(param.multipleValor),
            hasList: new FormControl(Boolean(param.parametroLista)),
            value: this.initParamValue(param, isMultiple, defaultValue),
          })
        );
      }
    });
  }

  /**
   * A function that sets the initial parameter value
   * @returns {FormControl | FormArray}
   */
  initParamValue(param: ParametroReporte, isMultiple?: boolean, defaultValue?: string): FormControl | FormArray {
    let value;
    if (isMultiple) {
      value = new FormArray(this.setDefaultValueMultiple(defaultValue), this.setValidator(param));
    } else {
      switch (param.parametro.tipoParametro) {
        case TipoParametro.date:
          value = new FormControl(this.setDefaultValueDate(defaultValue), this.setValidator(param));
          break;
        case TipoParametro.boolean:
          value = new FormControl(this.setDefaultValueBoolean(defaultValue), this.setValidator(param));
          break;
        default: {
          const defaultSystemUser = this.validateSystemUser(param.parametro.nombre) && this.authService.user?.email ? this.authService.user.email : defaultValue;
          value = new FormControl(this.setDefaultValue(defaultSystemUser), this.setValidator(param));
          break;
        }
      }
    }
    return value
  }

  /**
* Validates if the parameter is SYSTEM_USER
* @param {string} paramName
* @returns {boolean}
*/
  validateSystemUser(paramName: string): boolean { // skipcq: JS-0105
    return HelperFunctions.validateSystemUser(paramName);
  }

  /**
   * A function that set the validator if the parameter is required
   * @param {ParametroReporte} parameter
   * @returns {Validators | null}
   */
  setValidator(parameter: ParametroReporte): Validators | null { // skipcq: JS-0105

    return parameter.requerido ? Validators.required : null;
  }

  /**
   * A function that set the default value
   * @param {string[]} defaultValue
   * @returns {string | null}
   */
  setDefaultValue(defaultValue?: string): string | null { // skipcq: JS-0105

    return defaultValue ? defaultValue : null;
  }

  setDefaultValueMultiple(defaultValue?: string): FormControl[] { // skipcq: JS-0105

    return defaultValue ? [new FormControl(defaultValue)] : [];
  }

  /**
   * A function that set the default value for a date
   * @param {string[]} defaultValue
   * @returns {NgbDate | null}
   */
  setDefaultValueDate(defaultValue?: string): NgbDate | null { // skipcq: JS-0105

    return HelperFunctions.getNgDate(defaultValue);
  }

  /**
 * A function that set the default value for a boolean
 * @param {string[]} defaultValue
 * @returns {boolean | null}
 */
  setDefaultValueBoolean(defaultValue?: string): boolean | null { // skipcq: JS-0105

    let value = false;
    if (defaultValue && defaultValue.toLocaleLowerCase() === "true") {
      value = true;
    }
    return value;
  }

  /**
   * Get method to obtaiin the parameters form array
   * @returns {FormArray}
   */
  get parameters(): FormArray {
    return this.reportForm.get('parameters') as FormArray;
  }

  /**
 * A function that gets the parameters values
 * @param {AbstractControl} parameter
 * @returns {FormArray}
 */
  getParametersValues(parameter: AbstractControl): FormArray { // skipcq: JS-0105

    return parameter.get('value') as FormArray;
  }

  /**
   * A function that get the parameter info by id
   * @param {number} id
   * @returns {Parameter}
   */
  getParamInfo(id: number): ParametroReporte | undefined {
    const parameter = this.parameters.controls[id];
    return this.report.parametros?.find((param) => parameter.get('id')?.value === param.parametro.uuid);
  }

  /**
 * A function that gets the parameter value
 * @param {AbstractControl} parameter
 * @returns {FormControl}
 */
  getParameterValue(parameter: AbstractControl): FormControl { // skipcq: JS-0105

    return parameter.get('value') as FormControl;
  }

  /**
   * A function that create TareaReporte object to send to the backend
   * @returns {TareaReporte}
   */
  setReportTask(): TareaReporte {
    const parameters: ParametroTarea[] = this.parameters
      .getRawValue()
      .map((param) => {
        const parametro: ParametroTarea = {
          parametro: param.id,
          multipleValor: param.isMultiple
        };

        if (param.value !== null) {
          const value = param.type === TipoParametro.date && !param.hasList ?
            HelperFunctions.getStringOfNgbDate(param.value) :
            param.value.toString();

          if (value.trim().length > 0) {
            parametro.valor = value;
          }
        }
        return parametro;
      });

    const tareaReporte: TareaReporte = {
      uuid: uuidv4(),
      reporte: this.report.uuid,
      usuario: this.authService.user?.uuid ?? '',
      parametros: parameters
    };
    return tareaReporte;
  }

  /**
   * A function that create a customInput
   * @returns {CustomInput}
   */
  getCustomInput(index: number) {
    const parametro = this.getParamInfo(index);
    const customInput: CustomInput = {
      id: parametro?.parametro?.uuid ?? '',
      type: parametro?.parametro?.tipoParametro ?? '',
      required: parametro?.requerido,
      label: parametro?.alias,
      isList: Boolean(parametro?.parametroLista),
      options: parametro?.parametroLista?.valores,
      style: "width:100%;",
    }
    return customInput;
  }

  /**
   * A function that set the array of parameters to send the request
   * @returns None
   */
  onSubmit(): void {
    if (this.reportForm.valid) {
      this.isLoading = true;
      this.ngxUiLoaderService.start('report-parameters');
      this.reportService
        .generateReportTask(this.setReportTask())
        .pipe(
          delay(500),
          finalize(() => { this.isLoading = false; this.ngxUiLoaderService.stop('report-parameters'); }))
        .subscribe({
          next: () => this.ngbActiveModal.close({ message: REPORT_MESSAGES.GENERATE.REPORT, confirmTitle: "Aceptar" }),
          error: (error) => this.ngbActiveModal.close({ message: error.error.message, confirmTitle: "Cerrar", isError: true }),
        });
    } else {
      this.reportForm.markAllAsTouched();
    }
  }
}
